import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelM } from '@sorare/core/src/atoms/typography';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { ScarcityLabel } from '@sorare/core/src/components/collections/ScarcityLabel';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { waitMs } from '@sorare/core/src/lib/animation/waitMs';
import { getTeamColor } from '@sorare/core/src/lib/collections';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { LevelUpAnimation } from 'components/card/levelup/LevelUpAnimation';

import { AnimatedBonusProgressBar } from '../AnimatedBonusProgressBar';
import { AnimatedCardsNumber } from '../AnimatedCardsNumber';
import { EmptyCollectionSlot } from '../EmptyCollectionSlot';
import { cardsScoreCount } from '../utils';
import type {
  SlideIntoCollectionQuery,
  SlideIntoCollectionQueryVariables,
  SlideIntoCollection_anyCardInterface,
  SlideIntoCollection_cardCollection,
} from './__generated__/index.graphql';

const Wrapper = styled.div`
  width: 100%;
  min-width: 100%;
  overflow: hidden;
  height: var(--100vh);
  scroll-behavior: smooth;
  @media ${tabletAndAbove} {
    max-height: 85vh;
    height: 100%;
  }
`;

const HeaderWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
`;
const Header = styled(Vertical)`
  background: linear-gradient(
      180deg,
      rgba(var(--c-rgb-black), 0.2),
      rgba(var(--c-rgb-black), 0.2)
    ),
    linear-gradient(
      180deg,
      var(--background-color) 0%,
      rgba(var(--c-rgb-black), 0.8) 100%
    ),
    var(--c-black);
  padding: var(--double-unit);
  border-bottom: 1px solid var(--c-nl-700-opaque);
  min-height: 163px;
`;

const SLOT_COLUMN_GAP = 16;
const SlotsList = styled.div`
  display: grid;
  padding: var(--double-unit);
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: ${SLOT_COLUMN_GAP}px;
  row-gap: var(--triple-unit);
  transform: translateY(var(--translate-y, 0px));
  transition: transform 0.8s cubic-bezier(0.04, 0.61, 0.37, 0.97);
`;

const ProgressBarWrapper = styled.div`
  align-self: stretch;
`;

const ClubLogo = styled.img`
  object-fit: contain;
`;

type Props = {
  cardCollectionSlug: string;
  newCards: SlideIntoCollection_anyCardInterface[];
  onAnimationEnd: () => void;
  shouldAnimate: boolean;
};

const cardCollectionFragment = gql`
  fragment SlideIntoCollection_cardCollection on CardCollection {
    slug
    anyTeam {
      pictureUrl
      slug
      name
      styleGuideColor
    }
    sportSeason {
      name
    }
    rarity
    slotsCount
    slots {
      id
      cardPictureUrl
      anyPlayer {
        slug
      }
    }
    userCardCollection(forUserSlug: $userSlug) {
      id
      score
      slots {
        slug
        cardCollectionCardsCount
        slot {
          id
        }
        highlightedCardCollectionCard {
          id
          anyCard {
            slug
            pictureUrl
          }
        }
      }
    }
    bonusThresholds {
      threshold
    }
    ...AnimatedBonusProgressBar_cardCollection
  }
  ${AnimatedBonusProgressBar.fragments.cardCollection}
` as TypedDocumentNode<SlideIntoCollection_cardCollection>;

const SLIDE_INTO_COLLECTION_QUERY = gql`
  query SlideIntoCollectionQuery($slug: String!, $userSlug: String!) {
    cardCollection(slug: $slug) {
      id
      slug
      ...SlideIntoCollection_cardCollection
    }
  }
  ${cardCollectionFragment}
` as TypedDocumentNode<
  SlideIntoCollectionQuery,
  SlideIntoCollectionQueryVariables
>;

export const SlideIntoCollection = ({
  cardCollectionSlug,
  newCards,
  shouldAnimate,
  onAnimationEnd: onAnimationEndProp,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const { currentUser } = useCurrentUserContext();

  const { data, loading } = useQuery(SLIDE_INTO_COLLECTION_QUERY, {
    variables: {
      slug: cardCollectionSlug,
      userSlug: currentUser?.slug || '',
    },
  });

  const {
    rarity,
    anyTeam,
    sportSeason,
    slotsCount,
    slots,
    userCardCollection,
    bonusThresholds,
  } = data?.cardCollection || {};
  const { name: seasonName } = sportSeason || {};
  const { name: teamName, styleGuideColor, pictureUrl } = anyTeam || {};

  const newCardsSlots = newCards
    .map(card => {
      const slot = slots?.find(s => s.anyPlayer.slug === card.anyPlayer.slug);
      return {
        ...slot,
        pictureUrl: card.pictureUrl,
      };
    })
    .filter(Boolean);
  const [animatingSlotIndex, setAnimatingSlotIndex] = useState<number>();
  const [animatingSlotBonusIndex, setAnimatingSlotBonusIndex] = useState<
    number | null
  >(null);
  const [animationEnded, setAnimationEnded] = useState(false);
  const [levelUpAnimationLaunched, setLevelUpAnimationLaunched] =
    useState(false);
  const [lastThresholdReached, setLastThresholdReached] = useState(false);

  const [slotsListY, setSlotsListY] = useState<number>(0);

  useEffect(() => {
    if (!shouldAnimate || loading) {
      return;
    }
    (async () => {
      await waitMs(1100);
      setAnimatingSlotIndex(0);
    })();
  }, [shouldAnimate, loading]);

  const onAnimationEnd = async () => {
    if (typeof animatingSlotIndex !== 'number') {
      return;
    }
    await waitMs(300);
    if (animatingSlotIndex < newCardsSlots.length - 1) {
      setAnimatingSlotIndex(animatingSlotIndex + 1);
      return;
    }
    setAnimationEnded(true);
    onAnimationEndProp();
  };

  const initialFulfilledSlotsCount = userCardCollection?.slots.filter(
    slot =>
      slot.cardCollectionCardsCount > 0 &&
      newCardsSlots.every(newCardSlot => newCardSlot.id !== slot.slot.id)
  ).length;

  return (
    <Wrapper ref={ref}>
      <HeaderWrapper>
        <LevelUpAnimation
          animate={levelUpAnimationLaunched}
          onAnimationEnd={() => {
            setLevelUpAnimationLaunched(false);
            if (lastThresholdReached) {
              onAnimationEnd();
            }
          }}
          borderRadius="0"
        >
          <Header
            style={
              {
                '--background-color': getTeamColor(styleGuideColor),
              } as CSSProperties
            }
            center
          >
            <Horizontal center>
              {!!pictureUrl && (
                <ClubLogo src={pictureUrl} alt="" width={60} height={60} />
              )}
              <Vertical>
                <Horizontal center>
                  <LabelM bold>
                    {!!rarity && <ScarcityLabel scarcity={rarity} />}
                  </LabelM>
                  <LabelM bold>{seasonName}</LabelM>
                </Horizontal>
                <HeadlineM>{teamName}</HeadlineM>
              </Vertical>
            </Horizontal>
            <ProgressBarWrapper>
              {!!data?.cardCollection && (
                <AnimatedBonusProgressBar
                  cardCollection={data.cardCollection}
                  newCards={newCards}
                  animatingSlotIndex={animatingSlotBonusIndex}
                  onAnimationEnd={onAnimationEnd}
                  onThresholdReached={isLastThreshold => {
                    if (isLastThreshold) {
                      setLastThresholdReached(true);
                    }
                    setLevelUpAnimationLaunched(true);
                  }}
                />
              )}
            </ProgressBarWrapper>
            {typeof slotsCount === 'number' && (
              <AnimatedCardsNumber
                ownedCards={initialFulfilledSlotsCount || 0}
                addedCards={
                  typeof animatingSlotBonusIndex === 'number'
                    ? animatingSlotBonusIndex + 1
                    : 0
                }
                totalCards={slotsCount}
              />
            )}
          </Header>
        </LevelUpAnimation>
      </HeaderWrapper>
      <SlotsList
        style={
          {
            '--translate-y': `-${slotsListY}px`,
          } as CSSProperties
        }
      >
        {slots?.map((slot, index) => {
          const userCardCollectionSlot = userCardCollection?.slots.find(
            userSlot => userSlot.slot.id === slot.id
          );
          const isFilled = !!userCardCollectionSlot?.cardCollectionCardsCount;
          const newCardSlot = newCardsSlots.find(s => s.id === slot.id);
          if (isFilled && !newCardSlot) {
            return (
              <CardImg
                key={slot.id}
                src={
                  userCardCollectionSlot?.highlightedCardCollectionCard?.anyCard
                    .pictureUrl
                }
                width={320}
              />
            );
          }

          return (
            <EmptyCollectionSlot
              key={slot.id}
              cardPictureUrl={
                /* prefer card.pictureUrl because
                 * in some edge case it is not the same as the rest (eg. during EA reveal)
                 */
                newCardSlot?.pictureUrl ||
                userCardCollectionSlot?.highlightedCardCollectionCard?.anyCard
                  .pictureUrl ||
                slot?.cardPictureUrl
              }
              isNewCardSlot={!!newCardSlot}
              shouldAnimate={
                !!newCardSlot &&
                typeof animatingSlotIndex === 'number' &&
                newCardsSlots[animatingSlotIndex]?.id === slot.id &&
                !animationEnded
              }
              scrollContainerRef={ref}
              onScroll={async itemHeight => {
                const scoreBeforeAnimation =
                  (userCardCollection?.score || 0) -
                  cardsScoreCount(newCards.slice(animatingSlotIndex || 0));
                const lastThresholdAlreadyReached =
                  scoreBeforeAnimation >=
                  (bonusThresholds?.at(-1)?.threshold || Infinity);
                if (lastThresholdAlreadyReached && animatingSlotIndex !== 0) {
                  await waitMs(800);
                }
                setSlotsListY(
                  Math.floor(index / 2) * (itemHeight + SLOT_COLUMN_GAP)
                );
                await waitMs(800);

                if (lastThresholdAlreadyReached) {
                  onAnimationEnd();
                }
                setAnimatingSlotBonusIndex(prev => {
                  if (typeof prev !== 'number') {
                    return 0;
                  }
                  if (prev >= newCardsSlots.length - 1) {
                    return null;
                  }
                  return prev + 1;
                });
              }}
            />
          );
        })}
      </SlotsList>
    </Wrapper>
  );
};

SlideIntoCollection.fragments = {
  cardCollection: cardCollectionFragment,
  anyCardInterface: gql`
    fragment SlideIntoCollection_anyCardInterface on AnyCardInterface {
      slug
      pictureUrl
      anyPlayer {
        slug
      }
      ...AnimatedBonusProgressBar_anyCardInterface
      ...cardsScoreCount_anyCardInterface
    }
    ${AnimatedBonusProgressBar.fragments.anyCardInterface}
    ${cardsScoreCount.fragments.anyCardInterface}
  ` as TypedDocumentNode<SlideIntoCollection_anyCardInterface>,
};
